import { getValueAsPercentage } from "util/number-utils";
import { observer } from "mobx-react";

import TransitionDropArea from "./transitionDropArea";
import { TransitionDropAreasContainer } from "./transitionDropAreas.styles";

/**
 * @typedef TransitionDropAreasProps
 * @property {EditorUIStore} ui
 * @property {LayerStore} layer
 */
/**
 * @summary - Responsible for positioning and rendering transition drop areas above a layer
 * @param {TransitionDropAreasProps} props
 * @returns {React.Element}
 */
const TransitionDropAreas = ({ ui, layer }) => {
    const timeAsDurationPercentage = (time) => {
        return parseFloat(
            getValueAsPercentage(time, ui.timelineDuration).toFixed(4)
        );
    };

    const transitionDropAreas = [];
    layer.sectionsArraySorted.forEach((section) => {
        const extraProps = {
            layer,
            section,
            ui
        };

        const sectionStartTimePercent = timeAsDurationPercentage(
            section.startTime
        );
        const sectionEndTimePercent = timeAsDurationPercentage(section.endTime);
        const sectionWidthPercent = timeAsDurationPercentage(section.duration);

        const sideTransitionProps = (left, width, side) => ({
            ...extraProps,
            style: {
                left: `${left}%`,
                width: `${width}%`,
                zIndex: 1
            },
            [side]: section.id
        });

        const crossTransitionProps = (left) => ({
            ...extraProps,
            style: {
                left: `${left}%`,
                width: 50,
                transform: `translateX(-50%)`,
                zIndex: 2
            },
            sectionFrom: section.id,
            sectionTo: section.siblingRight.id
        });

        transitionDropAreas.push(
            sideTransitionProps(
                sectionStartTimePercent,
                sectionWidthPercent / 2,
                `sectionTo`
            )
        );

        transitionDropAreas.push(
            sideTransitionProps(
                sectionEndTimePercent - sectionWidthPercent / 2,
                sectionWidthPercent / 2,
                `sectionFrom`
            )
        );

        if (section.siblingRight) {
            // Only render a cross transition drop area if there is space available (total width of both sections is > 50px)
            if (section.width > 25 && section.siblingRight.width > 25) {
                transitionDropAreas.push(
                    crossTransitionProps(sectionEndTimePercent)
                );
            }
        }
    });

    return (
        <TransitionDropAreasContainer layer={layer}>
            {transitionDropAreas.map((props, index) => (
                <TransitionDropArea {...props} key={index} />
            ))}
        </TransitionDropAreasContainer>
    );
};

export default observer(TransitionDropAreas);
